import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../services/api';
import FamilyCard from './FamilyCard';
import FamilyStatsSummary from './FamilyStatsSummary';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import { onDataRefresh } from '../utils/crossViewValidation';
import { loadCheckboxState, saveCheckboxState } from '../utils/checkboxPersistence';
import '../assets/AdminTutees.css';

const AdminTutees = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isTutorView = location.pathname.startsWith('/tutor/');
  const [tutees, setTutees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTutee, setEditingTutee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    race: '',
    address: '',
    parent: {
      name: '',
      phone: '',
      email: ''
    },
    school: '',
    level: '',
    year: new Date().getFullYear(),
    contactInfo: '',
    notes: ''
  });
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('code');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showInactive, setShowInactive] = useState(() => loadCheckboxState('admin_tutees', 'show_inactive', false));
  const [selectedTutees, setSelectedTutees] = useState([]);
  const [showBulkLevelModal, setShowBulkLevelModal] = useState(false);
  const [bulkLevel, setBulkLevel] = useState('');
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showSubjectListModal, setShowSubjectListModal] = useState(false);
  const [subjectList, setSubjectList] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [editingSubjectIndex, setEditingSubjectIndex] = useState(null);
  const [editingSubjectValue, setEditingSubjectValue] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [showRateSettingsModal, setShowRateSettingsModal] = useState(false);
  const [rateSettings, setRateSettings] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    // Load view preference from localStorage, default to 'list'
    const savedViewMode = localStorage.getItem('adminTutees_viewMode');
    return savedViewMode === 'family' ? 'family' : 'list';
  }); // 'list' or 'family'
  const [families, setFamilies] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [familySortBy, setFamilySortBy] = useState('familyNumber'); // 'familyNumber', 'revenue', 'tutees'
  const [retryCount, setRetryCount] = useState(0);
  const [retryTimeout, setRetryTimeout] = useState(null);
  
  // Data caching for view switches (Requirement 13.3)
  const [familiesCache, setFamiliesCache] = useState(null);
  const [familiesCacheTimestamp, setFamiliesCacheTimestamp] = useState(null);

  // Finance IC assignment popup after tutee creation
  const [showFinanceICPrompt, setShowFinanceICPrompt] = useState(false);
  const [newlyCreatedTutee, setNewlyCreatedTutee] = useState(null);
  const [eligibleFinanceICs, setEligibleFinanceICs] = useState([]);
  const [selectedFinanceICForNew, setSelectedFinanceICForNew] = useState('');
  const [assigningFinanceIC, setAssigningFinanceIC] = useState(false);
  // Payment IC assignment (PayNow collector) in the same post-create popup
  const [paymentICs, setPaymentICs] = useState([]);
  const [selectedPaymentICForNew, setSelectedPaymentICForNew] = useState('');
  // "Add new" Payment IC inline fields (name first, then number)
  const [addingNewPaymentIC, setAddingNewPaymentIC] = useState(false);
  const [newPaymentICName, setNewPaymentICName] = useState('');
  const [newPaymentICNumber, setNewPaymentICNumber] = useState('');
  const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    fetchTutees();
    
    // Set up data refresh listener (Requirement 14.5)
    // When family data is updated elsewhere, refresh this view
    const cleanup = onDataRefresh((detail) => {
      console.log('[AdminTutees] Data refresh event received:', detail);
      
      // Refresh families if in family view
      if (viewMode === 'family') {
        console.log('[AdminTutees] Refreshing families due to data update');
        fetchFamilies(true); // Force refresh to bypass cache
      }
      
      // Also refresh tutees list to keep it in sync
      fetchTutees();
    });
    
    // Cleanup listener on unmount
    return cleanup;
  }, []);

  useEffect(() => {
    if (viewMode === 'family') {
      fetchFamilies();
    }
  }, [viewMode]);

  // Persist view mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('adminTutees_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close FAB menu if clicking outside
      if (!event.target.closest('.fab-container')) {
        setShowFabMenu(false);
      }
      
      // Clear selection if clicking outside table and FAB
      if (!event.target.closest('.tutees-table') && 
          !event.target.closest('.fab-container') &&
          !event.target.closest('.tutee-form-overlay') &&
          !event.target.closest('.search-bar') &&
          !event.target.closest('.controls-bar')) {
        if (selectedTutees.length > 0) {
          setSelectedTutees([]);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedTutees.length]);

  const fetchTutees = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Set timeout for 30 seconds
      const timeoutId = setTimeout(() => {
        throw new Error('Request timeout. Please check your connection and try again.');
      }, 30000);
      
      setRetryTimeout(timeoutId);
      
      const response = await adminApi.getTutees();
      clearTimeout(timeoutId);
      setRetryTimeout(null);
      
      setTutees(response.data);
      setError('');
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
        setRetryTimeout(null);
      }
      
      console.error('Error fetching tutees:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setError(`Failed to fetch tutees: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies = async (forceRefresh = false) => {
    try {
      console.log('Fetching families...');
      
      // Check if we have valid cached data (less than 5 minutes old)
      // Implements Requirement 13.3: Data caching for view switches
      if (!forceRefresh && familiesCache && familiesCacheTimestamp) {
        const cacheAge = Date.now() - familiesCacheTimestamp;
        if (cacheAge < CACHE_DURATION_MS) {
          console.log(`Using cached families data (age: ${Math.round(cacheAge / 1000)}s)`);
          setFamilies(familiesCache);
          return;
        } else {
          console.log(`Cache expired (age: ${Math.round(cacheAge / 1000)}s), fetching fresh data`);
        }
      }
      
      setFamiliesLoading(true);
      setError('');
      
      // Set timeout for 30 seconds
      const timeoutId = setTimeout(() => {
        throw new Error('Request timeout. Please check your connection and try again.');
      }, 30000);
      
      setRetryTimeout(timeoutId);
      
      const response = await adminApi.getFamilies();
      clearTimeout(timeoutId);
      setRetryTimeout(null);
      
      console.log('Families response:', response.data);
      const familiesData = response.data.families;
      
      // Update state and cache
      setFamilies(familiesData);
      setFamiliesCache(familiesData);
      setFamiliesCacheTimestamp(Date.now());
      
      setError('');
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
        setRetryTimeout(null);
      }
      
      console.error('Error fetching families:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setError(`Failed to load families: ${errorMessage}`);
    } finally {
      setFamiliesLoading(false);
    }
  };

  /**
   * Retry handler with exponential backoff
   * Implements Requirements 8.3
   * Max 3 retries with delays: 1s, 2s, 4s
   */
  const handleRetry = async () => {
    if (retryCount >= 3) {
      setError('Maximum retry attempts reached. Please contact support if the problem persists.');
      return;
    }

    // Calculate exponential backoff delay: 2^retryCount seconds
    const delay = Math.pow(2, retryCount) * 1000;
    
    console.log(`Retrying in ${delay}ms (attempt ${retryCount + 1}/3)...`);
    
    // Wait for the backoff delay
    await new Promise(resolve => setTimeout(resolve, delay));
    
    setRetryCount(prev => prev + 1);
    
    // Retry the appropriate fetch based on current view
    // Force refresh on retry to bypass cache
    if (viewMode === 'family') {
      await fetchFamilies(true);
    } else {
      await fetchTutees();
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('parent.')) {
      const parentField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        parent: {
          ...prev.parent,
          [parentField]: value
        }
      }));
    } else {
      setFormData(prev => {
        const updated = { ...prev, [name]: value };
        
        // If dateOfBirth is changed, calculate and update age
        if (name === 'dateOfBirth') {
          updated.age = calculateAge(value);
        }
        
        return updated;
      });
    }
    
    if (formError) {
      setFormError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTutee) {
        await adminApi.updateTutee(editingTutee._id, formData);
        setSuccess('Tutee updated successfully!');
      } else {
        const response = await adminApi.createTutee(formData);
        setSuccess('Tutee created successfully!');
        
        // Show Finance IC + Payment IC assignment popup for newly created tutee
        const createdTutee = response.data;
        setNewlyCreatedTutee(createdTutee);
        try {
          const [icResponse, paymentICResponse] = await Promise.all([
            adminApi.getEligibleFinanceICs(),
            adminApi.getPaymentICs()
          ]);
          const ics = icResponse.data?.data || icResponse.data || [];
          setEligibleFinanceICs(ics);
          setPaymentICs(paymentICResponse.data?.data || []);
        } catch (icErr) {
          console.error('Failed to fetch eligible Finance ICs / Payment ICs:', icErr);
          setEligibleFinanceICs([]);
          setPaymentICs([]);
        }
        setShowFinanceICPrompt(true);
      }
      
      resetForm();
      fetchTutees();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setFormError(errorMessage);
    }
  };

  // Assign both the Finance IC (monitor/sender) and the Payment IC (PayNow
  // collector) to the newly created tutee. Either may be left blank.
  const handleAssignFinanceICToNewTutee = async () => {
    if (!newlyCreatedTutee) return;
    if (!selectedFinanceICForNew && !selectedPaymentICForNew && !addingNewPaymentIC) {
      setError('Select a Finance IC and/or a Payment IC, or skip.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setAssigningFinanceIC(true);
    try {
      // 1. Resolve the Payment IC — create a new one first if adding inline.
      let paymentICId = selectedPaymentICForNew;
      if (addingNewPaymentIC) {
        if (!newPaymentICName.trim() || !newPaymentICNumber.trim()) {
          setError('Enter both the PayNow name and number for the new Payment IC.');
          setTimeout(() => setError(''), 3000);
          setAssigningFinanceIC(false);
          return;
        }
        const created = await adminApi.createPaymentIC({
          paynowName: newPaymentICName.trim(),
          paynowNumber: newPaymentICNumber.trim()
        });
        paymentICId = created.data?.data?._id;
        // Refresh the local list so it's available going forward
        setPaymentICs(prev => [...prev, created.data.data]);
      }

      // 2. Assign Finance IC (if chosen)
      if (selectedFinanceICForNew) {
        await adminApi.assignDefaultFinanceIC([newlyCreatedTutee._id], selectedFinanceICForNew, false);
      }

      // 3. Assign Payment IC (if chosen/created)
      if (paymentICId) {
        await adminApi.assignDefaultPaymentIC([newlyCreatedTutee._id], paymentICId, false);
      }

      setSuccess(`Assignment saved for ${newlyCreatedTutee.name}.`);
      handleSkipFinanceICAssignment();
      fetchTutees();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setError(`Failed to save assignment: ${errorMessage}`);
      setTimeout(() => setError(''), 4000);
    } finally {
      setAssigningFinanceIC(false);
    }
  };

  const handleSkipFinanceICAssignment = () => {
    setShowFinanceICPrompt(false);
    setNewlyCreatedTutee(null);
    setSelectedFinanceICForNew('');
    setSelectedPaymentICForNew('');
    setAddingNewPaymentIC(false);
    setNewPaymentICName('');
    setNewPaymentICNumber('');
  };

  const handleEdit = (tutee) => {
    setEditingTutee(tutee);
    setFormData({
      name: tutee.name || '',
      phone: tutee.phone || '',
      email: tutee.email || '',
      dateOfBirth: tutee.dateOfBirth || '',
      gender: tutee.gender || '',
      race: tutee.race || '',
      address: tutee.address || '',
      parent: {
        name: tutee.parent?.name || '',
        phone: tutee.parent?.phone || '',
        email: tutee.parent?.email || ''
      },
      school: tutee.school || '',
      level: tutee.level || '',
      year: tutee.year || new Date().getFullYear(),
      contactInfo: tutee.contactInfo || '',
      notes: tutee.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      dateOfBirth: '',
      gender: '',
      race: '',
      address: '',
      parent: {
        name: '',
        phone: '',
        email: ''
      },
      school: '',
      level: '',
      year: new Date().getFullYear(),
      contactInfo: '',
      notes: ''
    });
    setEditingTutee(null);
    setShowForm(false);
    setFormError('');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelectTutee = (tuteeId) => {
    setSelectedTutees(prev => 
      prev.includes(tuteeId)
        ? prev.filter(id => id !== tuteeId)
        : [...prev, tuteeId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTutees.length === filteredTutees.length) {
      setSelectedTutees([]);
    } else {
      setSelectedTutees(filteredTutees.map(t => t._id));
    }
  };

  const handleBulkArchive = async () => {
    if (selectedTutees.length === 0) return;
    
    if (window.confirm(`Archive ${selectedTutees.length} tutee(s)? They will be marked as inactive but data will be preserved.`)) {
      try {
        await adminApi.bulkArchiveTutees(selectedTutees);
        setSuccess(`${selectedTutees.length} tutee(s) archived successfully!`);
        setSelectedTutees([]);
        await fetchTutees();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to archive tutees: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleBulkUnarchive = async () => {
    if (selectedTutees.length === 0) return;
    
    if (window.confirm(`Reactivate ${selectedTutees.length} tutee(s)?`)) {
      try {
        await adminApi.bulkUnarchiveTutees(selectedTutees);
        setSuccess(`${selectedTutees.length} tutee(s) reactivated successfully!`);
        setSelectedTutees([]);
        await fetchTutees();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to reactivate tutees: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleBulkUpdateLevels = async () => {
    if (!bulkLevel) {
      setFormError('Please select a level');
      return;
    }
    
    try {
      await adminApi.bulkUpdateTuteeLevels(selectedTutees, bulkLevel, bulkYear);
      setSuccess(`${selectedTutees.length} tutee(s) updated successfully!`);
      setSelectedTutees([]);
      setShowBulkLevelModal(false);
      setBulkLevel('');
      setBulkYear(new Date().getFullYear());
      await fetchTutees();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setFormError(`Failed to update tutee levels: ${errorMessage}`);
    }
  };

  const fetchSubjectList = async () => {
    try {
      const response = await adminApi.getSubjectList();
      setSubjectList(response.data.subjects || []);
    } catch (err) {
      console.error('Failed to fetch subject list:', err);
    }
  };

  const handleAddSubject = () => {
    if (!newSubject.trim()) return;
    
    if (subjectList.includes(newSubject.trim())) {
      setFormError('This subject already exists');
      return;
    }
    
    setSubjectList([...subjectList, newSubject.trim()]);
    setNewSubject('');
    setFormError('');
  };

  const handleRemoveSubject = (subject) => {
    setSubjectList(subjectList.filter(s => s !== subject));
  };

  const handleEditSubject = (index) => {
    setEditingSubjectIndex(index);
    setEditingSubjectValue(subjectList[index]);
  };

  const handleSaveEditSubject = (index) => {
    if (!editingSubjectValue.trim()) {
      setFormError('Subject name cannot be empty');
      return;
    }
    
    if (subjectList.includes(editingSubjectValue.trim()) && editingSubjectValue.trim() !== subjectList[index]) {
      setFormError('This subject already exists');
      return;
    }
    
    const updatedList = [...subjectList];
    updatedList[index] = editingSubjectValue.trim();
    setSubjectList(updatedList);
    setEditingSubjectIndex(null);
    setEditingSubjectValue('');
    setFormError('');
  };

  const handleCancelEditSubject = () => {
    setEditingSubjectIndex(null);
    setEditingSubjectValue('');
    setFormError('');
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newList = [...subjectList];
    const draggedItem = newList[draggedIndex];
    newList.splice(draggedIndex, 1);
    newList.splice(index, 0, draggedItem);
    
    setSubjectList(newList);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSaveSubjectList = async () => {
    try {
      await adminApi.updateSubjectList(subjectList);
      setSuccess('Subject list updated successfully!');
      setShowSubjectListModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setFormError(`Failed to update subject list: ${errorMessage}`);
    }
  };

  const handleOpenSubjectList = async () => {
    await fetchSubjectList();
    setShowSubjectListModal(true);
  };

  const fetchRateSettings = async () => {
    try {
      const response = await adminApi.getTutorRateSettings();
      setRateSettings(response.data.rates || {});
    } catch (err) {
      console.error('Failed to fetch rate settings:', err);
      setFormError('Failed to load rate settings');
    }
  };

  const handleOpenRateSettings = async () => {
    await fetchRateSettings();
    setShowRateSettingsModal(true);
  };

  const handleRateChange = (level, mode, value) => {
    setRateSettings(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [mode]: parseFloat(value) || 0
      }
    }));
  };

  const handleSaveRateSettings = async () => {
    try {
      await adminApi.updateTutorRateSettings(rateSettings);
      setSuccess('Rate settings updated successfully!');
      setShowRateSettingsModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setFormError(`Failed to update rate settings: ${errorMessage}`);
    }
  };

  const viewTuteeDetails = (tuteeId) => {
    if (isTutorView) {
      navigate(`/tutor/admin-tutees/${tuteeId}`);
    } else {
      navigate(`/admin/tutees/${tuteeId}`);
    }
  };

  /**
   * Handle family card click - navigate to family detail page
   * Implements Property 28: Family Card Navigation
   */
  const handleFamilyCardClick = (familyNumber) => {
    navigate(`/admin/families/${familyNumber}`);
  };

  /**
   * Handle tutee chip click - navigate to tutee profile
   * Implements Property 29: Tutee Chip Navigation
   */
  const handleTuteeChipClick = (e, tuteeId) => {
    e.stopPropagation();
    if (isTutorView) {
      navigate(`/tutor/admin-tutees/${tuteeId}`);
    } else {
      navigate(`/admin/tutees/${tuteeId}`);
    }
  };

  const handleArchive = async (tuteeId, tuteeName, e) => {
    e.stopPropagation();
    if (window.confirm(`Archive tutee ${tuteeName}? They will be marked as inactive but data will be preserved.`)) {
      try {
        await adminApi.deleteTutee(tuteeId);
        setSuccess('Tutee archived successfully! Enable "Show inactive" to view archived tutees.');
        await fetchTutees();
        setTimeout(() => setSuccess(''), 5000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to archive tutee: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleActivate = async (tuteeId, tuteeName, e) => {
    e.stopPropagation();
    if (window.confirm(`Reactivate ${tuteeName}?`)) {
      try {
        await adminApi.updateTutee(tuteeId, { isActive: true });
        setSuccess('Tutee reactivated successfully!');
        await fetchTutees();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to reactivate tutee: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleDelete = async (tuteeId, tuteeName, e) => {
    e.stopPropagation();
    if (window.confirm(`⚠️ PERMANENTLY DELETE tutee ${tuteeName}? This action cannot be undone and will remove all data.`)) {
      if (window.confirm(`Are you absolutely sure? Type the tutee's name to confirm deletion.`)) {
        try {
          await adminApi.permanentlyDeleteTutee(tuteeId);
          setSuccess('Tutee permanently deleted!');
          await fetchTutees();
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
          setError(`Failed to delete tutee: ${errorMessage}`);
          setTimeout(() => setError(''), 3000);
        }
      }
    }
  };

  const getFilteredAndSortedTutees = () => {
    let filtered = tutees.filter(tutee => {
      // Filter by active/inactive - treat undefined as active (default)
      const isActive = tutee.isActive !== false;
      if (!showInactive && !isActive) return false;
      
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          tutee.name.toLowerCase().includes(search) ||
          tutee.school?.toLowerCase().includes(search) ||
          tutee.parent?.name?.toLowerCase().includes(search)
        );
      }
      return true;
    });

    // Sort by the selected field
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortField) {
        case 'code':
          // Natural sorting for codes: SIN-A2-1 comes before SIN-A10-1
          const aCode = a.code || '';
          const bCode = b.code || '';
          
          // Extract parts: SIN-A10-1 -> ['SIN', 'A', '10', '1']
          const aMatch = aCode.match(/^([A-Z]+)-([A-Z])(\d+)-(\d+)$/);
          const bMatch = bCode.match(/^([A-Z]+)-([A-Z])(\d+)-(\d+)$/);
          
          if (aMatch && bMatch) {
            // Compare prefix (SIN)
            if (aMatch[1] !== bMatch[1]) {
              return aMatch[1] < bMatch[1] ? (sortDirection === 'asc' ? -1 : 1) : (sortDirection === 'asc' ? 1 : -1);
            }
            // Compare letter (A, B, C)
            if (aMatch[2] !== bMatch[2]) {
              return aMatch[2] < bMatch[2] ? (sortDirection === 'asc' ? -1 : 1) : (sortDirection === 'asc' ? 1 : -1);
            }
            // Compare family number numerically (2 vs 10)
            const aNum = parseInt(aMatch[3], 10);
            const bNum = parseInt(bMatch[3], 10);
            if (aNum !== bNum) {
              return aNum < bNum ? (sortDirection === 'asc' ? -1 : 1) : (sortDirection === 'asc' ? 1 : -1);
            }
            // Compare child number numerically (1 vs 2)
            const aChild = parseInt(aMatch[4], 10);
            const bChild = parseInt(bMatch[4], 10);
            return aChild < bChild ? (sortDirection === 'asc' ? -1 : 1) : (sortDirection === 'asc' ? 1 : -1);
          }
          
          // Fallback to string comparison if format doesn't match
          aVal = aCode;
          bVal = bCode;
          break;
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'school':
          aVal = a.school?.toLowerCase() || '';
          bVal = b.school?.toLowerCase() || '';
          break;
        case 'parent':
          aVal = a.parent?.name?.toLowerCase() || '';
          bVal = b.parent?.name?.toLowerCase() || '';
          break;
        case 'level':
          aVal = a.level?.toLowerCase() || '';
          bVal = b.level?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  /**
   * Filter and sort families based on search term and sort criteria
   * Implements Property 24 (Multi-Field Search) and Properties 25, 26, 27 (Sort Order)
   */
  const getFilteredAndSortedFamilies = () => {
    let filtered = families;

    // Apply search filter (Property 24: Multi-Field Search)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = families.filter(family => {
        return (
          family.familyCode.toLowerCase().includes(search) ||
          family.parentName.toLowerCase().includes(search) ||
          family.parentPhone.toLowerCase().includes(search) ||
          family.tutees.some(tutee => tutee.name.toLowerCase().includes(search))
        );
      });
    }

    // Apply sort (Properties 25, 26, 27: Sort Order)
    filtered = [...filtered].sort((a, b) => {
      switch(familySortBy) {
        case 'familyNumber':
          // Property 25: Sort by family number (ascending)
          return a.familyNumber - b.familyNumber;
        
        case 'revenue':
          // Property 26: Sort by monthly revenue (descending - highest first)
          return (b.monthlyRevenue || 0) - (a.monthlyRevenue || 0);
        
        case 'tutees':
          // Property 27: Sort by number of tutees (descending - most first)
          return b.totalTutees - a.totalTutees;
        
        default:
          return 0;
      }
    });

    return filtered;
  };

  if (loading) return <LoadingSpinner message="Loading tutees..." />;
  if (error && !familiesLoading) return <ErrorMessage message={error} onRetry={handleRetry} />;

  const filteredTutees = getFilteredAndSortedTutees();

  return (
    <div className="admin-tutees">
      <div className="page-header">
        <div className="header-title">
          <h1>Tutee Management</h1>
          <p className="subtitle">{tutees.length} total tutees • {tutees.filter(t => t.isActive !== false).length} active</p>
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="controls-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder={viewMode === 'family' ? "Search by family code, parent name, phone, or tutee name..." : "Search by name, school, or parent..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <div className="view-toggle">
            <button 
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>List</span>
            </button>
            <button 
              className={`view-toggle-btn ${viewMode === 'family' ? 'active' : ''}`}
              onClick={() => {
                console.log('Families button clicked, switching to family view');
                setViewMode('family');
              }}
              title="Family View"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M3 3h6v6H3zM11 3h6v6h-6zM3 11h6v6H3zM11 11h6v6h-6z" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              <span>Families</span>
            </button>
          </div>
          <div className="controls-spacer">
            {viewMode === 'family' && (
              <select
                className="sort-select"
                value={familySortBy}
                onChange={(e) => setFamilySortBy(e.target.value)}
                title="Sort families"
              >
                <option value="familyNumber">Sort by Family Number</option>
                <option value="revenue">Sort by Revenue</option>
                <option value="tutees">Sort by Tutees</option>
              </select>
            )}
            {viewMode === 'list' && (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowInactive(checked);
                    saveCheckboxState('admin_tutees', 'show_inactive', checked);
                  }}
                />
                Show inactive
              </label>
            )}
          </div>
          <button className="btn-settings" onClick={handleOpenRateSettings} title="Manage Tutor Rate Settings">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12z" fill="currentColor"/>
              <path d="M10 6v4l3 2" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span>Rates</span>
          </button>
          <button className="btn-settings" onClick={handleOpenSubjectList} title="Manage Subject List">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>Subjects</span>
          </button>
          <button className="btn-add-icon" onClick={() => setShowForm(!showForm)} title="Add New Tutee">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {selectedTutees.length > 0 && (
        <div className="fab-container">
          <div className={`fab-menu ${showFabMenu ? 'open' : ''}`}>
            <button className="fab-menu-item" onClick={() => { setShowBulkLevelModal(true); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 3v14M3 10h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Update Level</span>
            </button>
            <button className="fab-menu-item" onClick={() => { handleBulkArchive(); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 6h14M5 6v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6M8 9v4M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Archive</span>
            </button>
            <button className="fab-menu-item" onClick={() => { handleBulkUnarchive(); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 3v6M10 13v4M3 10h6M13 10h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Unarchive</span>
            </button>
            <button className="fab-menu-item clear" onClick={() => { setSelectedTutees([]); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 5l10 10M15 5l-10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Clear</span>
            </button>
          </div>
          <button 
            className={`fab-button ${showFabMenu ? 'open' : ''}`}
            onClick={() => setShowFabMenu(!showFabMenu)}
            title={`${selectedTutees.length} tutee${selectedTutees.length > 1 ? 's' : ''} selected`}
          >
            <div className="fab-count">{selectedTutees.length}</div>
            <svg className="fab-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}
      
      {showBulkLevelModal && (
        <div className="tutee-form-overlay" onClick={() => setShowBulkLevelModal(false)}>
          <div className="tutee-form-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="popup-header">
              <h3>Update Level for {selectedTutees.length} Tutee{selectedTutees.length > 1 ? 's' : ''}</h3>
              <button className="close-btn" onClick={() => setShowBulkLevelModal(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <div className="form-group">
                <label htmlFor="bulkLevel">New Level *</label>
                <select
                  id="bulkLevel"
                  value={bulkLevel}
                  onChange={(e) => setBulkLevel(e.target.value)}
                  required
                >
                  <option value="">Select level</option>
                  <option value="Kindergarten 1">Kindergarten 1</option>
                  <option value="Kindergarten 2">Kindergarten 2</option>
                  <option value="Primary 1">Primary 1</option>
                  <option value="Primary 2">Primary 2</option>
                  <option value="Primary 3">Primary 3</option>
                  <option value="Primary 4">Primary 4</option>
                  <option value="Primary 5">Primary 5</option>
                  <option value="Primary 6">Primary 6</option>
                  <option value="Secondary 1">Secondary 1</option>
                  <option value="Secondary 2">Secondary 2</option>
                  <option value="Secondary 3">Secondary 3</option>
                  <option value="Secondary 4">Secondary 4</option>
                  <option value="Secondary 5">Secondary 5</option>
                  <option value="JC 1">JC 1</option>
                  <option value="JC 2">JC 2</option>
                  <option value="Polytechnic Year 1">Polytechnic Year 1</option>
                  <option value="Polytechnic Year 2">Polytechnic Year 2</option>
                  <option value="Polytechnic Year 3">Polytechnic Year 3</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="bulkYear">Year</label>
                <input
                  type="number"
                  id="bulkYear"
                  value={bulkYear}
                  onChange={(e) => setBulkYear(parseInt(e.target.value))}
                  placeholder="e.g., 2025"
                  min="2020"
                  max="2040"
                />
                <small className="field-hint">Which year are they in this level</small>
              </div>

              {formError && <div className="form-error">{formError}</div>}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowBulkLevelModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={handleBulkUpdateLevels}>
                  Update {selectedTutees.length} Tutee{selectedTutees.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSubjectListModal && (
        <div className="tutee-form-overlay" onClick={() => setShowSubjectListModal(false)}>
          <div className="tutee-form-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="popup-header">
              <h3>Manage Subject List</h3>
              <button className="close-btn" onClick={() => setShowSubjectListModal(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <div className="form-group">
                <label>Add New Subject</label>
                <div className="add-subject-row">
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSubject()}
                    placeholder="Enter subject name"
                    className="subject-input"
                  />
                  <button type="button" className="btn-add-subject" onClick={handleAddSubject}>
                    Add
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Current Subjects ({subjectList.length})</label>
                <div className="subject-list">
                  {subjectList.length === 0 ? (
                    <p style={{ color: '#999', fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
                      No subjects yet. Add your first subject above.
                    </p>
                  ) : (
                    subjectList.map((subject, index) => (
                      <div 
                        key={index} 
                        className={`subject-item ${draggedIndex === index ? 'dragging' : ''}`}
                        draggable={editingSubjectIndex !== index}
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="drag-handle" title="Drag to reorder">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="6" cy="4" r="1.5" fill="currentColor"/>
                            <circle cx="10" cy="4" r="1.5" fill="currentColor"/>
                            <circle cx="6" cy="8" r="1.5" fill="currentColor"/>
                            <circle cx="10" cy="8" r="1.5" fill="currentColor"/>
                            <circle cx="6" cy="12" r="1.5" fill="currentColor"/>
                            <circle cx="10" cy="12" r="1.5" fill="currentColor"/>
                          </svg>
                        </div>
                        {editingSubjectIndex === index ? (
                          <>
                            <input
                              type="text"
                              className="subject-edit-input"
                              value={editingSubjectValue}
                              onChange={(e) => setEditingSubjectValue(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSaveEditSubject(index);
                                if (e.key === 'Escape') handleCancelEditSubject();
                              }}
                              autoFocus
                            />
                            <div className="subject-edit-actions">
                              <button
                                type="button"
                                className="btn-save-edit"
                                onClick={() => handleSaveEditSubject(index)}
                                title="Save"
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                className="btn-cancel-edit"
                                onClick={handleCancelEditSubject}
                                title="Cancel"
                              >
                                ×
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <span>{subject}</span>
                            <div className="subject-actions">
                              <button
                                type="button"
                                className="btn-edit-subject"
                                onClick={() => handleEditSubject(index)}
                                title="Edit subject"
                              >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                  <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="btn-remove"
                                onClick={() => handleRemoveSubject(subject)}
                                title="Remove subject"
                              >
                                ×
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {formError && <div className="form-error">{formError}</div>}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowSubjectListModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={handleSaveSubjectList}>
                  Save Subject List
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRateSettingsModal && (
        <div className="tutee-form-overlay" onClick={() => setShowRateSettingsModal(false)}>
          <div className="tutee-form-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="popup-header">
              <h3>Tutor Rate Settings</h3>
              <button className="close-btn" onClick={() => setShowRateSettingsModal(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                Set default payment rates for tutors by education level and mode. These rates are what you pay tutors (not what clients pay).
              </p>

              <div className="rate-settings-grid">
                <div className="rate-grid-header">
                  <div className="rate-grid-cell header-cell">Level</div>
                  <div className="rate-grid-cell header-cell">Physical ($/hr)</div>
                  <div className="rate-grid-cell header-cell">Online ($/hr)</div>
                </div>

                {/* Kindergarten */}
                <div className="rate-group-header">Kindergarten</div>
                {['Kindergarten 1', 'Kindergarten 2'].map((level) => (
                  <div key={level} className="rate-grid-row">
                    <div className="rate-grid-cell level-cell">{level}</div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.physical || ''}
                        onChange={(e) => handleRateChange(level, 'physical', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.online || ''}
                        onChange={(e) => handleRateChange(level, 'online', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                  </div>
                ))}

                {/* Primary */}
                <div className="rate-group-header">Primary School</div>
                {['Primary 1', 'Primary 2', 'Primary 3', 'Primary 4', 'Primary 5', 'Primary 6'].map((level) => (
                  <div key={level} className="rate-grid-row">
                    <div className="rate-grid-cell level-cell">{level}</div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.physical || ''}
                        onChange={(e) => handleRateChange(level, 'physical', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.online || ''}
                        onChange={(e) => handleRateChange(level, 'online', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                  </div>
                ))}

                {/* Secondary */}
                <div className="rate-group-header">Secondary School</div>
                {['Secondary 1', 'Secondary 2', 'Secondary 3', 'Secondary 4', 'Secondary 5'].map((level) => (
                  <div key={level} className="rate-grid-row">
                    <div className="rate-grid-cell level-cell">{level}</div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.physical || ''}
                        onChange={(e) => handleRateChange(level, 'physical', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.online || ''}
                        onChange={(e) => handleRateChange(level, 'online', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                  </div>
                ))}

                {/* Junior College */}
                <div className="rate-group-header">Junior College</div>
                {['JC 1', 'JC 2'].map((level) => (
                  <div key={level} className="rate-grid-row">
                    <div className="rate-grid-cell level-cell">{level}</div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.physical || ''}
                        onChange={(e) => handleRateChange(level, 'physical', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.online || ''}
                        onChange={(e) => handleRateChange(level, 'online', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                  </div>
                ))}

                {/* Polytechnic */}
                <div className="rate-group-header">Polytechnic</div>
                {['Polytechnic Year 1', 'Polytechnic Year 2', 'Polytechnic Year 3'].map((level) => (
                  <div key={level} className="rate-grid-row">
                    <div className="rate-grid-cell level-cell">{level}</div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.physical || ''}
                        onChange={(e) => handleRateChange(level, 'physical', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.online || ''}
                        onChange={(e) => handleRateChange(level, 'online', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                  </div>
                ))}

                {/* Others */}
                <div className="rate-group-header">Others</div>
                {['Others'].map((level) => (
                  <div key={level} className="rate-grid-row">
                    <div className="rate-grid-cell level-cell">{level}</div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.physical || ''}
                        onChange={(e) => handleRateChange(level, 'physical', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                    <div className="rate-grid-cell">
                      <input
                        type="number"
                        className="rate-input"
                        value={rateSettings[level]?.online || ''}
                        onChange={(e) => handleRateChange(level, 'online', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="5"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '20px', padding: '15px', background: '#dbeafe', borderRadius: '8px', borderLeft: '3px solid var(--info)' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                  <strong>Example:</strong> If a client pays $60/hr for JC Math and you set JC tutor rate to $50/hr, 
                  your commission is $10/hr (split between 2 co-founders = $5/hr each).
                </p>
              </div>

              {formError && <div className="form-error">{formError}</div>}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowRateSettingsModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={handleSaveRateSettings}>
                  Save Rate Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="tutee-form-overlay" onClick={resetForm}>
          <div className="tutee-form-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>{editingTutee ? 'Edit Tutee' : 'Add New Tutee'}</h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="name">Tutee Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter tutee's full name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Tutee Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+65 1234 5678"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Tutee Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="tutee@example.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth (Age)</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Gender</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="race">Race</label>
                  <input
                    type="text"
                    id="race"
                    name="race"
                    value={formData.race}
                    onChange={handleInputChange}
                    placeholder="e.g., Chinese, Malay, Indian"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="e.g., 123 Main Street, Singapore 123456"
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Parent/Guardian Information</h4>
                <div className="form-group">
                  <label htmlFor="parent.name">Parent Name *</label>
                  <input
                    type="text"
                    id="parent.name"
                    name="parent.name"
                    value={formData.parent.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Parent or guardian name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="parent.phone">Parent Phone *</label>
                    <input
                      type="tel"
                      id="parent.phone"
                      name="parent.phone"
                      value={formData.parent.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+65 1234 5678"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="parent.email">Parent Email</label>
                    <input
                      type="email"
                      id="parent.email"
                      name="parent.email"
                      value={formData.parent.email}
                      onChange={handleInputChange}
                      placeholder="parent@example.com"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>School Information</h4>
                <div className="form-group">
                  <label htmlFor="school">School *</label>
                  <input
                    type="text"
                    id="school"
                    name="school"
                    value={formData.school}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Raffles Institution"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="year">Year *</label>
                    <input
                      type="number"
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      placeholder="e.g., 2025"
                      min="2020"
                      max="2040"
                      required
                    />
                    <small className="field-hint">Which year is the tutee in this level</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="level">Level *</label>
                    <select
                      id="level"
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select level</option>
                      <option value="Kindergarten 1">Kindergarten 1</option>
                      <option value="Kindergarten 2">Kindergarten 2</option>
                      <option value="Primary 1">Primary 1</option>
                      <option value="Primary 2">Primary 2</option>
                      <option value="Primary 3">Primary 3</option>
                      <option value="Primary 4">Primary 4</option>
                      <option value="Primary 5">Primary 5</option>
                      <option value="Primary 6">Primary 6</option>
                      <option value="Secondary 1">Secondary 1</option>
                      <option value="Secondary 2">Secondary 2</option>
                      <option value="Secondary 3">Secondary 3</option>
                      <option value="Secondary 4">Secondary 4</option>
                      <option value="Secondary 5">Secondary 5</option>
                      <option value="JC 1">JC 1</option>
                      <option value="JC 2">JC 2</option>
                      <option value="Polytechnic Year 1">Polytechnic Year 1</option>
                      <option value="Polytechnic Year 2">Polytechnic Year 2</option>
                      <option value="Polytechnic Year 3">Polytechnic Year 3</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </div>


              </div>

              <div className="form-section">
                <h4>Additional Information</h4>

                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Any additional notes about the tutee..."
                  />
                </div>
              </div>
              
              {formError && <div className="form-error">{formError}</div>}
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTutee ? 'Update Tutee' : 'Create Tutee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {viewMode === 'family' ? (
        <div className="family-view-section">
          {familiesLoading ? (
            <LoadingSpinner message="Loading families..." />
          ) : error ? (
            <ErrorMessage message={error} onRetry={handleRetry} />
          ) : (
            <>
              <FamilyStatsSummary
                totalFamilies={families.length}
                totalTutees={families.reduce((sum, f) => sum + f.totalTutees, 0)}
                totalActiveSubjects={families.reduce((sum, f) => sum + f.totalActiveSubjects, 0)}
                totalMonthlyRevenue={families.reduce((sum, f) => sum + (f.monthlyRevenue || 0), 0)}
              />

              <div className="families-grid">
                {getFilteredAndSortedFamilies().map(family => {
                  // Debug: Log first family to check data structure
                  if (family.familyNumber === 1) {
                    console.log('Sample family data:', family);
                    console.log('Sample tutee data:', family.tutees[0]);
                  }
                  return (
                    <FamilyCard
                      key={family.familyNumber}
                      family={family}
                      onClick={handleFamilyCardClick}
                      onTuteeClick={handleTuteeChipClick}
                    />
                  );
                })}
              </div>

              {getFilteredAndSortedFamilies().length === 0 && (
                <div className="no-families">
                  {searchTerm ? 'No families match your search.' : 'No families found.'}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="tutees-list" style={{ width: '100%', display: 'block' }}>
          {filteredTutees.length === 0 ? (
            <div className="no-data">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="30" stroke="#e0e0e0" strokeWidth="2"/>
                <path d="M32 20v24M20 32h24" stroke="#e0e0e0" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p>{searchTerm ? 'No tutees match your search.' : 'No tutees found. Click "Add Tutee" to get started.'}</p>
            </div>
          ) : (
          <div className="tutees-table-container" style={{ width: '100%', display: 'block' }}>
            <table className="tutees-table" style={{ width: '100%', tableLayout: 'fixed', display: 'table' }}>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedTutees.length === filteredTutees.length && filteredTutees.length > 0}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th onClick={() => handleSort('name')} className="sortable">
                    <div className="th-content">
                      Tutee Name
                      {sortField === 'name' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('code')} className="sortable">
                    <div className="th-content">
                      Code
                      {sortField === 'code' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('school')} className="sortable">
                    <div className="th-content">
                      School
                      {sortField === 'school' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('parent')} className="sortable">
                    <div className="th-content">
                      Parent
                      {sortField === 'parent' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('level')} className="sortable">
                    <div className="th-content">
                      Level
                      {sortField === 'level' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Subjects</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTutees.map((tutee) => (
                  <tr key={tutee._id} onClick={() => viewTuteeDetails(tutee._id)} className="clickable-row">
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedTutees.includes(tutee._id)}
                        onChange={() => toggleSelectTutee(tutee._id)}
                      />
                    </td>
                    <td>
                      <div className="tutee-info">
                        <div className="tutee-avatar">{tutee.name.charAt(0).toUpperCase()}</div>
                        <div>
                          <div className="tutee-name">{tutee.name}</div>
                          {tutee.email && <div className="tutee-meta">{tutee.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      {tutee.code ? (
                        <span className="tutee-code">{tutee.code}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{tutee.school || <span className="text-muted">-</span>}</td>
                    <td>
                      {tutee.parent?.name ? (
                        <div className="parent-info">
                          <div className="parent-name">{tutee.parent.name}</div>
                          {tutee.parent.phone && <div className="parent-phone">{tutee.parent.phone}</div>}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{tutee.level || <span className="text-muted">-</span>}</td>
                    <td>
                      {tutee.subjects && tutee.subjects.length > 0 ? (
                        <div className="subject-count">
                          {tutee.subjects.filter(s => s.isActive !== false).length} active
                        </div>
                      ) : (
                        <span className="text-muted">None</span>
                      )}
                    </td>
                    <td style={{ color: tutee.isActive !== false ? '#10b981' : '#dc2626', fontWeight: 'bold', fontSize: '14px' }}>
                      {tutee.isActive !== false ? '● Active' : '● Inactive'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button 
                          className="btn-action btn-view"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewTuteeDetails(tutee._id);
                          }}
                          title="View details"
                        >
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3C4.5 3 1.7 5.6 1 8c.7 2.4 3.5 5 7 5s6.3-2.6 7-5c-.7-2.4-3.5-5-7-5z" stroke="currentColor" strokeWidth="1.5"/>
                            <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                        </button>
                        {tutee.isActive !== false ? (
                          <button 
                            className="btn-action btn-archive"
                            onClick={(e) => handleArchive(tutee._id, tutee.name, e)}
                            title="Archive"
                          >
                            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                              <path d="M2 4h12M3 4v9a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4M6 7v4M10 7v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        ) : (
                          <>
                            <button 
                              className="btn-action btn-activate"
                              onClick={(e) => handleActivate(tutee._id, tutee.name, e)}
                              title="Reactivate"
                            >
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                                <path d="M8 2v4M8 10v4M2 8h4M10 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                            <button 
                              className="btn-action btn-delete"
                              onClick={(e) => handleDelete(tutee._id, tutee.name, e)}
                              title="Delete permanently"
                            >
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                                <path d="M2 4h12M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M13 4v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4h10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      )}

      {/* Finance IC Assignment Popup - shown after creating a new tutee */}
      {showFinanceICPrompt && newlyCreatedTutee && (
        <div className="finance-ic-prompt-overlay" onClick={handleSkipFinanceICAssignment}>
          <div className="finance-ic-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="finance-ic-prompt-header">
              <div className="finance-ic-prompt-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Tutee Created Successfully!</h3>
              <p className="finance-ic-prompt-subtitle">
                <strong>{newlyCreatedTutee.name}</strong> has been added. Would you like to assign a Finance IC now?
              </p>
            </div>

            <div className="finance-ic-prompt-body">
              {/* Finance IC (monitors/sends invoices) */}
              <label className="finance-ic-select-label">Finance IC (monitors &amp; sends)</label>
              {eligibleFinanceICs.length > 0 ? (
                <select
                  className="finance-ic-select"
                  value={selectedFinanceICForNew}
                  onChange={(e) => setSelectedFinanceICForNew(e.target.value)}
                >
                  <option value="">-- Choose a Finance IC --</option>
                  {eligibleFinanceICs.map((ic) => (
                    <option key={ic._id} value={ic._id}>
                      {ic.name}{ic.email ? ` (${ic.email})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="finance-ic-no-ics">
                  No eligible Finance ICs found. You can assign one later.
                </p>
              )}

              {/* Payment IC (whose PayNow collects payment) */}
              <label className="finance-ic-select-label" style={{ marginTop: '1rem' }}>
                Payment IC (collects PayNow)
              </label>
              {!addingNewPaymentIC ? (
                <>
                  <select
                    className="finance-ic-select"
                    value={selectedPaymentICForNew}
                    onChange={(e) => {
                      if (e.target.value === '__add_new__') {
                        setAddingNewPaymentIC(true);
                        setSelectedPaymentICForNew('');
                      } else {
                        setSelectedPaymentICForNew(e.target.value);
                      }
                    }}
                  >
                    <option value="">-- Choose a Payment IC --</option>
                    {paymentICs.map((ic) => (
                      <option key={ic._id} value={ic._id}>
                        {ic.paynowName} — {ic.paynowNumber}
                      </option>
                    ))}
                    <option value="__add_new__">➕ Add new Payment IC…</option>
                  </select>
                </>
              ) : (
                <div className="payment-ic-add-new">
                  <input
                    type="text"
                    className="finance-ic-select"
                    placeholder="PayNow name (e.g. SRI JUNARSIH)"
                    value={newPaymentICName}
                    onChange={(e) => setNewPaymentICName(e.target.value)}
                  />
                  <input
                    type="text"
                    className="finance-ic-select"
                    style={{ marginTop: '0.5rem' }}
                    placeholder="PayNow number (e.g. 98241850)"
                    value={newPaymentICNumber}
                    onChange={(e) => setNewPaymentICNumber(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-skip-finance-ic"
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => {
                      setAddingNewPaymentIC(false);
                      setNewPaymentICName('');
                      setNewPaymentICNumber('');
                    }}
                  >
                    Cancel new Payment IC
                  </button>
                </div>
              )}
            </div>

            <div className="finance-ic-prompt-actions">
              <button
                className="btn-skip-finance-ic"
                onClick={handleSkipFinanceICAssignment}
              >
                Skip for now
              </button>
              <button
                className="btn-assign-finance-ic"
                onClick={handleAssignFinanceICToNewTutee}
                disabled={
                  assigningFinanceIC ||
                  (!selectedFinanceICForNew && !selectedPaymentICForNew && !addingNewPaymentIC)
                }
              >
                {assigningFinanceIC ? 'Saving...' : 'Save Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTutees;
